import { inject } from '@angular/core';
import { CanActivateFn, RedirectCommand, Router } from '@angular/router';
import { AuthService } from '../services/authentication.service';
import { buildLoginRedirectUrl } from '../utils/url-utils';
import { Constants } from '../constants/constants';

export const AuthenticatedAndAuthorizedGuard: CanActivateFn = async (route, state): Promise<boolean | RedirectCommand> => {
    const authService = inject(AuthService);
    const router = inject(Router);

    // If user is not authenticated
    if (!authService.isAuthenticated()) {
        // loadSessionFromToken try to restore session. If token exists and is valid return true
        const authenticated = await authService.loadSessionFromToken();

        if (!authenticated) {
            // Interceptor will have redirected to login if a 401 error was received (invalid token)
            // If no 401 error (token not exist), redirect to login

            // state.url is the URL the user is trying to access.
            // router.routerState.snapshot.url is the current stable URL.
            // Avoid redirecting if already on login page or a redirect to login is in progress (e.g., by interceptor).
            const currentStableUrl = router.routerState.snapshot.url;
            if (!currentStableUrl.startsWith('/login')) {
                const companyCodeFromStorage = localStorage.getItem('lastUserLoginBrand');
                const loginUrl = buildLoginRedirectUrl(state.url, companyCodeFromStorage);
                return new RedirectCommand(router.parseUrl(loginUrl));
            }
            return false;
        }
    }

    // User is correctly authenticated, get their info
    const user: any = authService.getUserSession();

    // Get the route roles
    const requiredRoles = route.data?.['roles'] as string[];

    // No roles defined in route, pass to additional custom checks
    let hasBaseAccess = true;
    if (requiredRoles && requiredRoles.length > 0) {
        // Check if user has any of the roles required by the route
        hasBaseAccess = user.roles?.some((role: string) => requiredRoles.includes(role));
    }

    if (!hasBaseAccess) {
        // User has no valid roles for the route
        return new RedirectCommand(router.parseUrl('/forbidden'));
    }

    // -------------------------------------------------------------------------
    // CUSTOM RESTRICTIONS FOR SPECIFIC MODULES (ADD-ON CHECK)
    // -------------------------------------------------------------------------
    const userRoles = authService.getUserRoles();
    const userEditorPermissions = authService.getUserEditorPermissions();

    // Clean target URL from query parameters to evaluate path cleanly
    const targetUrl = state.url.split('?')[0];

    // [CUSTOM CHECK 1]: Global Statistics Section Restriction
    const isNavigatingToStatistics = targetUrl === '/statistics' || targetUrl.startsWith('/statistics/');
    if (isNavigatingToStatistics) {
        // If the user does not have explicit stats view permission, block navigation
        if (!userEditorPermissions || !userEditorPermissions.viewStatisticsAllowed) {
            return new RedirectCommand(router.parseUrl('/forbidden'));
        }
    }

    // [CUSTOM CHECK 2]: Content Feeds Restriction for "MANAGER" role
    const isOnlyManager =
        userRoles.includes(Constants.USER_ROLES.manager) &&
        !userRoles.includes(Constants.USER_ROLES.admin) &&
        !userRoles.includes(Constants.USER_ROLES.publisher);

    if (isOnlyManager) {
        const feed = userEditorPermissions?.availableFeeds;

        // A) Messages & Questionnaires (Belongs to TIMELINE feed or ALL)
        const isMessagesOrQuestionnaires =
            targetUrl === '/messages' || targetUrl.startsWith('/messages/') || targetUrl === '/questionnaires' || targetUrl.startsWith('/questionnaires/');

        if (isMessagesOrQuestionnaires) {
            const hasTimelineAccess = feed === Constants.AVAILABLE_FEEDS.ALL || feed === Constants.AVAILABLE_FEEDS.TIMELINE;
            if (!hasTimelineAccess) {
                return new RedirectCommand(router.parseUrl('/forbidden'));
            }
        }

        // B) Knowledge Center (Belongs to KNOWLEDGE_CENTER feed or ALL)
        const isKnowledgeCenter = targetUrl === '/knowledge-center' || targetUrl.startsWith('/knowledge-center/');

        if (isKnowledgeCenter) {
            const hasKnowledgeCenterAccess = feed === Constants.AVAILABLE_FEEDS.ALL || feed === Constants.AVAILABLE_FEEDS.KNOWLEDGE_CENTER;
            if (!hasKnowledgeCenterAccess) {
                return new RedirectCommand(router.parseUrl('/forbidden'));
            }
        }
    }

    // User has passed all base role checks and all custom feed/stats restrictions
    return true;
};
